import { World, SessionMode, VisibilityState, createSystem, launchXR } from '@iwsdk/core';
import {
  startServiceRuntime,
  makeServiceBridgeSystem,
  type CreateSystemLike,
} from '@realitycollective/service-framework-iwsdk';
import { createServiceProfile } from '@realitycollective/service-framework';
import {
  createLiveKitRoomRegistrations,
  IAssistSessionService,
  IAssistStrokeService,
  IAssistantRosterService,
  IDetectionService,
  ILiveKitRoomService,
  ITrainingStateService,
  type ILiveKitRoomService as RoomService,
} from '@questvisionstream/client';
import {
  AppConfig,
  buildConfigUrl,
  readCell,
  readDevLiterals,
  readWearerGateOverride,
  resolveApiBase,
} from './config';
import { setServiceManager } from './runtime';
import { acquirePassthroughCamera, type CameraAcquisition } from './camera/acquirePassthroughCamera';
import { DEMO_CONFIG_URL, demoFetch, readDemoAutoplay, readDemoCameraDisabled, readDemoMode } from './demo/demoFixtures';
import { createDemoRoomFactory, syntheticCameraStream } from './demo/demoRoom';
import { renderWarmupCard, type WarmupCameraState, type WarmupState } from './warmup';
import { DetectionRenderSystem } from './systems/DetectionRenderSystem';
import { StrokeRenderSystem } from './systems/StrokeRenderSystem';
import { StatusSpriteSystem } from './systems/StatusSpriteSystem';
import { TrainingUxSystem } from './systems/TrainingUxSystem';
import { bindStatusDom, status, wireStatusServices } from './ui/status';
import { uiLog } from './ui/uiLog';

/**
 * Injected from package.json at build time by Vite (see vite.config.ts) - the
 * real deployed version, so it can never drift from a hand-edited constant.
 */
declare const __CLIENT_VERSION__: string;

/** Client build stamp, reported on connect so a stale deploy is obvious. */
export const CLIENT_BUILD = `v${__CLIENT_VERSION__}`;

/**
 * Wire the flat-page warm-up card - the web home of Unity's
 * `StartupFlowController` tiers (which were themselves ported from this page's
 * ancestor). All decisions live in the pure `renderWarmupCard`; this function
 * only feeds it state and applies its output to the DOM.
 *
 * The Enter press is the consent moment: it calls `beginStreaming()` (frames
 * may leave the device) and launches the immersive session in the same user
 * gesture. While the D24 waiting window is open the same button reads Join
 * and closes the window with an ordinary `connect()` instead.
 */
function wireWarmup(world: World, room: RoomService, acquireCamera: () => Promise<CameraAcquisition>): void {
  const intro = document.getElementById('intro');
  const button = document.getElementById('enter-xr') as HTMLButtonElement | null;
  const note = document.getElementById('intro-note');
  const addressRow = document.getElementById('address-row');
  const addressInput = document.getElementById('address-input') as HTMLInputElement | null;
  const addressOk = document.getElementById('address-ok') as HTMLButtonElement | null;
  if (!button) return;

  const state: {
    -readonly [K in keyof WarmupState]: WarmupState[K];
  } = {
    started: false,
    starting: false,
    xrChecked: false,
    xrSupported: false,
    cameraState: 'checking' as WarmupCameraState,
    cameraError: null,
    roomState: room.connectionState,
    waiting: room.availability.isWaiting,
    availability: null,
    discoveryFailure: null,
  };

  /** Set once the address prompt has been shown, so it stays visible while a retry runs rather than flickering away. */
  let addressPromptShown = false;
  let currentAction: 'none' | 'enter' | 'join' = 'none';

  const render = (): void => {
    const cardState: typeof state = { ...state, availability: room.availability.describe(Date.now()) };
    if (!room.availability.isWaiting && room.availability.phase !== 'blocked') {
      cardState.availability = null;
    }
    cardState.waiting = room.availability.isWaiting;
    const view = renderWarmupCard(cardState);

    intro?.classList.toggle('hidden', !view.visible);
    button.textContent = view.buttonLabel;
    button.disabled = !view.buttonEnabled;
    currentAction = view.action;
    if (note) {
      note.textContent = view.note;
      note.classList.toggle('error', view.noteIsError);
    }

    // Once shown, the prompt stays up until a connection succeeds, so a
    // failed retry can be corrected without re-typing from scratch.
    if (view.showAddressPrompt) {
      if (!addressPromptShown) {
        addressPromptShown = true;
        if (addressInput && !addressInput.value) addressInput.value = room.configEndpoint;
      }
    } else if (state.roomState === 'connected') {
      addressPromptShown = false;
    }
    addressRow?.classList.toggle('visible', addressPromptShown && view.visible);
  };

  // ---- room state feeds the tiers.
  room.on('stateChange', (roomState) => {
    state.roomState = roomState;
    if (roomState === 'connected') state.discoveryFailure = null;
    render();
  });
  room.on('discoveryFailed', (reason) => {
    state.discoveryFailure = reason;
    render();
  });
  room.on('availabilityChanged', () => render());

  // ---- session lifecycle: the card hides on sessionstart, returns on end.
  const xr = (world as unknown as { renderer?: { xr?: EventTarget } }).renderer?.xr;
  xr?.addEventListener?.('sessionstart', () => {
    state.started = true;
    state.starting = false;
    render();
  });
  xr?.addEventListener?.('sessionend', () => {
    state.started = false;
    render();
  });

  // ---- the one button: Enter (consent + launch) or Join (close the window).
  button.addEventListener('click', () => {
    if (button.disabled) return;

    if (currentAction === 'join') {
      // Ordinary first attempt - may lose the claim race and land back in the
      // window, which is correct (the button is a hint, not a reservation).
      void room.connect();
      render();
      return;
    }

    if (currentAction !== 'enter') return;
    state.starting = true;
    render();
    try {
      // Consent first (frames may flow), then the session - one user gesture.
      room.beginStreaming();
      launchXR(world, { sessionMode: SessionMode.ImmersiveAR });
    } catch (err) {
      state.starting = false;
      if (note) {
        note.textContent = `Could not start XR: ${err instanceof Error ? err.message : String(err)}`;
        note.classList.add('error');
      }
      render();
    }
  });

  // ---- manual endpoint row (RA-C10's repurposed fallback).
  const submitAddress = (): void => {
    const value = addressInput?.value ?? '';
    if (!value.trim()) {
      if (note) {
        note.textContent = "Enter the server's address or URL.";
        note.classList.add('error');
      }
      return;
    }
    if (!room.useConfigEndpoint(value)) {
      if (note) {
        note.textContent = `'${value.trim()}' isn't a usable address - try a host name or URL.`;
        note.classList.add('error');
      }
      return;
    }
    state.discoveryFailure = null;
    state.roomState = room.connectionState;
    render();
  };
  addressOk?.addEventListener('click', submitAddress);
  addressInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') submitAddress();
  });

  // ---- tier 2: XR support (resolves once).
  const query = navigator.xr?.isSessionSupported?.('immersive-ar');
  if (!query) {
    state.xrChecked = true;
    state.xrSupported = false;
    render();
  } else {
    void query
      .then((supported) => {
        state.xrChecked = true;
        state.xrSupported = supported;
        render();
      })
      .catch(() => {
        state.xrChecked = true;
        state.xrSupported = false;
        render();
      });
  }

  // ---- tier 3: the camera pre-flight (never hangs - every await is capped).
  // One camera, one feed: the acquired stream IS the publish stream.
  void acquireCamera().then((result) => {
    if (result.ok) {
      state.cameraState = 'ready';
      status.set('camera', `ready (${result.deviceId.slice(0, 8)}…)`);
      room.setVideoStream(result.stream);
    } else {
      state.cameraState = 'error';
      state.cameraError = result.message;
      status.set('camera', `error - ${result.kind}`);
      console.warn(`[WebXRClient] Camera pre-flight failed (${result.kind}): ${result.message}`);
    }
    render();
  });

  render();
}

/**
 * Remote-assistance WebXR wearer client entry point.
 *
 *  1. Create the IWSDK world. No auto-offer: the flat page is the warm-up
 *     card, and nothing streams before its Enter.
 *  2. `startServiceRuntime` builds the service graph: the LiveKit room
 *     transport, detections, the assist services (consent, roster, strokes)
 *     and the server-authority training mirror.
 *  3. Register the framework's `ServiceBridgeSystem` (pumps ticks + focus and
 *     pause from the XR session) and the host systems: detection overlay,
 *     stroke renderer, status HUD, and the training UX glue.
 */
async function bootstrap(): Promise<void> {
  // `?demo=` runs the whole client against an in-memory room and fixture
  // Functions - zero LiveKit spend, no headset - for UX work and the
  // documentation screenshots. Never a production path.
  const demoMode = readDemoMode();
  const apiBase = resolveApiBase();
  const configUrl = demoMode ? DEMO_CONFIG_URL : buildConfigUrl(apiBase);
  const devLiterals = readDevLiterals();
  console.info(`[WebXRClient] ${CLIENT_BUILD} - config endpoint:`, configUrl, demoMode ? `(demo: ${demoMode})` : '');

  const statusPanel = document.getElementById('status');
  if (statusPanel) bindStatusDom(status, statusPanel);
  uiLog.mount();
  uiLog.push(`● ${CLIENT_BUILD} · config ${configUrl}`);

  const container = document.getElementById('scene-container') as HTMLDivElement;
  const world = await World.create(container, {
    render: { near: 0.01, far: 100 },
    xr: {
      sessionMode: SessionMode.ImmersiveAR,
      offer: 'none',
      features: {
        hitTest: { required: false },
        layers: true,
      },
    },
    features: {
      environmentRaycast: true,
      grabbing: false,
      locomotion: false,
    },
  });

  const { manager, adapter } = startServiceRuntime(world, () =>
    createServiceProfile('remote-assistance-wearer', [
      ...createLiveKitRoomRegistrations({
        configUrl,
        cell: readCell(),
        wearerToken: readWearerGateOverride(),
        devServerUrl: devLiterals.url,
        devToken: devLiterals.token,
        strokes: { invertY: AppConfig.invertY },
        training: { invertY: AppConfig.invertY },
        ...(demoMode
          ? { fetchImpl: demoFetch(demoMode), roomFactory: createDemoRoomFactory({ autoplay: readDemoAutoplay() }) }
          : {}),
      }),
    ]),
  );
  setServiceManager(manager);

  // The -iwsdk shim uses minimal structural contracts (it never imports
  // @iwsdk/core), so we bridge the concrete IWSDK types here: `createSystem`
  // and the returned bridge class are cast at this documented interop seam.
  const bridgeSystem = makeServiceBridgeSystem({
    adapter,
    manager,
    world,
    createSystem: createSystem as unknown as CreateSystemLike,
    visibleState: VisibilityState.Visible,
  });

  world
    .registerSystem(bridgeSystem as never)
    .registerSystem(DetectionRenderSystem) // detections topic -> red boxes (also records the capture pose)
    .registerSystem(StrokeRenderSystem) // strokes topic -> world lines through the same pose
    .registerSystem(StatusSpriteSystem) // in-AR status HUD (head-locked)
    .registerSystem(TrainingUxSystem); // step form, hand menu, assistant grid, world label

  const room = manager.resolve(ILiveKitRoomService);
  wireStatusServices(status, {
    room,
    detection: manager.resolve(IDetectionService),
    assist: manager.resolve(IAssistSessionService),
    roster: manager.resolve(IAssistantRosterService),
    strokes: manager.resolve(IAssistStrokeService),
    training: manager.resolve(ITrainingStateService),
  });
  room.on('diagnostic', (line) => uiLog.push(`● ${line}`));

  const acquireCamera =
    demoMode && !readDemoCameraDisabled()
      ? async (): Promise<CameraAcquisition> => ({ ok: true, stream: syntheticCameraStream(), deviceId: 'demo-camera' })
      : acquirePassthroughCamera;
  wireWarmup(world, room, acquireCamera);

  console.info('[WebXRClient] Ready.');
}

bootstrap().catch((err) => console.error('[WebXRClient] bootstrap failed', err));
